import { Course, University, StudentReview, Student } from '../types';
import { DEMO_COURSES, DEMO_UNIVERSITIES, DEMO_REVIEWS } from '../lib/demoData';

// --- Student Management ---

export const createOrGetStudent = async (
  name: string,
  phone: string,
  email?: string
): Promise<Student> => {
  // 1. Phone Validation Regex (US Format mostly, but flexible)
  // Matches: 555-555-5555, (555) 555-5555, 555 555 5555, 5555555555
  const phoneRegex = /^(\+?1[-. ]?)?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

  if (!phoneRegex.test(phone)) {
    throw new Error(`Invalid phone number format: ${phone}. Please use a valid 10-digit format like 555-123-4567.`);
  }

  // Standardize phone: Remove all non-digit characters
  const cleanPhone = phone.replace(/\D/g, '');
  const cleanEmail = email && email.trim() !== '' ? email.trim() : null;
  const cleanName = name.trim();

  // Simulate checking if student exists in localStorage
  const storedStudents = JSON.parse(localStorage.getItem('edu_students') || '[]');
  const existingStudent = storedStudents.find((student: Student) => student.phone === cleanPhone);

  if (existingStudent) {
    // Update email if provided and different
    if (cleanEmail && existingStudent.email !== cleanEmail) {
      existingStudent.email = cleanEmail;
      const updatedStudents = storedStudents.map((s: Student) => 
        s.id === existingStudent.id ? existingStudent : s
      );
      localStorage.setItem('edu_students', JSON.stringify(updatedStudents));
    }
    return existingStudent;
  }

  // Create new student
  const newStudent: Student = {
    id: `student-${Date.now()}`,
    name: cleanName,
    phone: cleanPhone,
    email: cleanEmail || undefined,
    created_at: new Date().toISOString()
  };

  // Store in localStorage
  storedStudents.push(newStudent);
  localStorage.setItem('edu_students', JSON.stringify(storedStudents));

  return newStudent;
};

// --- Course Management ---

export const getCourseById = (id: string): Course | undefined => {
  return DEMO_COURSES.find(course => course.id === id);
};

export const getAllCourses = async (): Promise<Course[]> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 200));
  return [...DEMO_COURSES];
};

export const getFeaturedCourses = async (): Promise<Course[]> => {
  // Return the first 4 courses as featured
  await new Promise(resolve => setTimeout(resolve, 200));
  return DEMO_COURSES.slice(0, 4);
};

export const getCoursesByCategory = async (category: string): Promise<Course[]> => {
  await new Promise(resolve => setTimeout(resolve, 200));
  return DEMO_COURSES.filter(course => course.category === category);
};

export const getCoursesByLevel = async (level: string): Promise<Course[]> => {
  await new Promise(resolve => setTimeout(resolve, 200));
  return DEMO_COURSES.filter(course => course.level === level);
};

// --- University Management ---

export const getUniversityById = (id: string): University | undefined => {
  return DEMO_UNIVERSITIES.find(university => university.id === id);
};

export const getAllUniversities = async (): Promise<University[]> => {
  await new Promise(resolve => setTimeout(resolve, 200));
  return [...DEMO_UNIVERSITIES];
};

export const getUniversitiesByCourse = async (courseId: string): Promise<University[]> => {
  await new Promise(resolve => setTimeout(resolve, 200));
  return DEMO_UNIVERSITIES.filter(university => 
    university.courses.includes(courseId)
  );
};


// --- Course Enrollment ---

export const enrollInCourse = async (studentId: string, courseId: string): Promise<void> => {
  const storedStudents = JSON.parse(localStorage.getItem('edu_students') || '[]');
  const studentIndex = storedStudents.findIndex((s: Student) => s.id === studentId);

  if (studentIndex !== -1) {
    if (!storedStudents[studentIndex].enrolled_courses) {
      storedStudents[studentIndex].enrolled_courses = [];
    }

    if (!storedStudents[studentIndex].enrolled_courses!.includes(courseId)) {
      storedStudents[studentIndex].enrolled_courses!.push(courseId);
      localStorage.setItem('edu_students', JSON.stringify(storedStudents));
    }
  }
};

// --- Reviews ---

export const getReviews = async () => {
  // Get reviews from localStorage
  const storedReviews = JSON.parse(localStorage.getItem('edu_reviews') || '[]');

  // Combine with demo reviews
  const { DEMO_REVIEWS } = await import('../lib/demoData');
  const allReviews = [...storedReviews, ...DEMO_REVIEWS];

  // Sort by date (newest first)
  return allReviews.sort((a: StudentReview, b: StudentReview) =>
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );
};